import { ReactNode, useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { fyStore, useFinancialYear as useFinancialYearShared } from '@esgcaffe/ui-common';
import api, { tokenManager } from '../services/api';

/**
 * Shell-side FY bootstrap.
 *
 * The shell is the only context that fetches the financial-year list from the API.
 * Results are pushed into the shared @esgcaffe/ui-common store, which propagates to
 * every loaded MFE via BroadcastChannel + localStorage — so each MFE sees the same
 * selection without having its own React Context tree or its own HTTP fetch.
 *
 * The hook is re-exported here so existing imports from `@/contexts/FinancialYearContext`
 * keep working unchanged.
 */

interface FinancialYearProviderProps {
  children: ReactNode;
}

export const FinancialYearProvider = ({ children }: FinancialYearProviderProps) => {
  const authToken = useSyncExternalStore(
    tokenManager.subscribeAuthToken,
    () => tokenManager.getToken() ?? '',
    () => '',
  );

  // Guard against duplicate fetches in StrictMode and re-renders.
  const lastFetchedTokenRef = useRef<string | null>(null);

  const fetchFinancialYears = useCallback(async () => {
    fyStore.setLoading(true);
    fyStore.setError(null);

    try {
      const response = await api.getFormattedFinancialYears();

      if (response.isSuccess && response.data) {
        fyStore.setFinancialYears(
          response.data.financialYears || [],
          response.data.startMonth || 4,
          response.data.startMonthName || 'April',
        );
      } else {
        fyStore.setError('Failed to fetch financial years');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch financial years';
      console.error('Error fetching financial years:', err);
      fyStore.setError(message);
    } finally {
      fyStore.setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authToken) {
      lastFetchedTokenRef.current = null;
      fyStore.reset();
      return;
    }
    if (lastFetchedTokenRef.current === authToken) return;
    lastFetchedTokenRef.current = authToken;
    void fetchFinancialYears();
  }, [authToken, fetchFinancialYears]);

  return <>{children}</>;
};

export const useFinancialYear = useFinancialYearShared;

export default FinancialYearProvider;
